import { formatDistanceToNow, differenceInSeconds, parseISO } from "date-fns";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";
import DraggableFlatList from "react-native-draggable-flatlist";
import { useDispatch } from "react-redux";

import CardAccount from "@/components/card/CardAccount";
import CardAddAccount from "@/components/card/CardAddAcount";
import AddAccountModal from "@/components/modal/banking/AddAccountModal";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { getAccountsIdsAndIndexes } from "@/utils/accounts/Utils";
import { setAccountsOrderInCookies } from "@/utils/cookies/Cookies";

interface Account {
  id: string;
  currentBalance: number;
  availableBalance: number;
  assetTypeId: string;
  depositInstructions: any[];
  status: string;
}

interface AccountSectionProps {
  data?: Account[];
  isAccountsOrderEditing?: boolean;
  clearFilter: () => void;
}

export default function AccountSection(props: AccountSectionProps) {
  const [accountsData, setAccountsData] = useState<Account[]>([]);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [isCardsDragging, setIsCardsDragging] = useState({});
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);

  const lastUpdate = globalValues.accountsLastUpdate
    ? parseISO(globalValues.accountsLastUpdate)
    : new Date();

  const secondsDifference = differenceInSeconds(new Date(), lastUpdate);
  const lessThanOneMinute = secondsDifference < 60;

  const timeUpdate = lessThanOneMinute
    ? t("common:placeholder.lastminute")
    : formatDistanceToNow(lastUpdate, { addSuffix: true });

  useEffect(() => {
    if (props.data && props.data.length > 0) {
      setAccountsData(props.data);
    }
  }, [props.data]);

  return (
    <ScrollView horizontal>
      <DraggableFlatList
        data={accountsData}
        className="pl-1 pt-1 pb-1 pr-0.5"
        horizontal
        renderItem={({ item, drag }) => {
          return (
            <CardAccount
              key={item.id}
              id={item.id}
              currentBalance={item.currentBalance}
              availableBalance={item.availableBalance}
              depositInstructions={item.depositInstructions}
              type={item.assetTypeId}
              status={item.status}
              lastUpdate={timeUpdate}
              isEditing={isCardsDragging}
              clearFilter={() => props.clearFilter()}
              activeLongPress={() => {
                setIsCardsDragging((prev) => ({
                  ...prev,
                  [item.id]: true,
                }));
                drag();
              }}
            />
          );
        }}
        keyExtractor={(item, index) => item.id}
        onDragEnd={({ data }) => {
          setAccountsData(data);
          setAccountsOrderInCookies(getAccountsIdsAndIndexes(data));
          setIsCardsDragging({});
        }}
      />
      <View className="py-0 pr-0 lg:py-1 lg:pr-1">
        <CardAddAccount onPress={() => setShowAddAccountModal(true)} />
      </View>
      <AddAccountModal
        visible={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onClick={(state) => setShowAddAccountModal(state)}
        hasChanged={(state) => dispatch(setForm({ accountHasChanges: state }))}
      />
    </ScrollView>
  );
}
